import { maskDate, maskMoneyReq, removeMaskMoney } from 'util/masks'
import { handlerError } from 'util/handlerError'
import { SagaIterator } from 'redux-saga'
import {
  call,
  fork,
  put,
  all,
  takeEvery,
  select,
  CallEffect
} from 'redux-saga/effects'
import { CAMPANHA, ParametrizacaoCampanhaReq, TYPES } from './types'
import * as campanhaController from 'controller/campanhaController'
import * as campanhaActions from 'store/modules/backoffice/campanha/actions'
import * as loadingActions from 'store/modules/loading/actions'
import { ApplicationState } from 'store'
import { toast } from 'react-toastify'
import { Cidade } from 'models/types'

// Import temporariamente desativado.
// import {
//   AbastecimentoCampanhaDto,
//   ReabastecimentoCampanhaDto
// } from 'models/backoffice/campanha/type'

interface GenericProps {
  type: string
  id: string
}

function resolveCategoriaSaida(
  register: CAMPANHA['register'],
  optionsSelect: CAMPANHA['optionsSelect']
) {
  const isUraOuDiscador =
    register.tipoCampanhaCrmId === 'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' ||
    register.tipoCampanhaCrmId === 'd38a3f12-927f-4936-8eee-6552ce2ed81e'

  if (!isUraOuDiscador) return null

  const empresasSelecionadas = register.empresasGrupo
    .map((idGrupoELoja) =>
      optionsSelect.empresasGrupos.find(
        (empresa) => empresa.idGrupoELoja === idGrupoELoja
      )
    )
    .filter((item) => !!item)

  const lojasSelecionadas = empresasSelecionadas.filter((item) =>
    item?.descricao?.startsWith('Loja -')
  )
  const temGrupoSelecionado = empresasSelecionadas.some((item) =>
    item?.descricao?.startsWith('Grupo -')
  )

  if (temGrupoSelecionado || lojasSelecionadas.length !== 1) return null

  return register.campanhaCrmParametrosDiscador.pabxipCategoriaSaida
}

function* getAllCampanhas(): SagaIterator {
  const { pagination, search }: CAMPANHA = yield select(
    (state: ApplicationState) => state.campanha
  )

  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(campanhaController.getAllCampanhas, {
      pagina: pagination.page,
      registros: pagination.pageSize,
      descricao: search.descricao || '',
      dataInicio: search.dataInicio || '',
      dataFim: search.dataFim || '',
      lojaId: search.lojaId ?? '',
      tipoCampanhaId: search.tipoCampanhaId ?? '',
      status: search.status
    })

    yield put(
      campanhaActions.setPaginacao({
        ...pagination,
        totalRegistros: response?.data?.content?.quantidadeTotal
      })
    )

    yield put(
      campanhaActions.setAllCampanhas(response?.data?.content?.registros)
    )
  } catch (error) {
    yield put(campanhaActions.cleanPagination())
    yield put(campanhaActions.cleanAllCampanhas())
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getCampanha({ id }: GenericProps): SagaIterator {
  try {
    yield call(campanhaController.getCampanha, id)
  } catch (error) {
    handlerError(error)
  }
}

function* visualizarCampanha({ id }: GenericProps): SagaIterator {
  yield put(loadingActions.setLoading(true))
  try {
    const response = yield call(campanhaController.getCampanha, id)
    const campanhaCrmParametrosElevenLabs =
      response?.data?.content.campanhaCrmParametrosElevenLabs

    const quantidadeInicialLimiteCartao =
      response?.data?.content.quantidadeInicialLimiteCartao ??
      response?.data?.content.quantidadeInicialMargemCartao

    const quantidadeFinalLimiteCartao =
      response?.data?.content.quantidadeFinalLimiteCartao ??
      response?.data?.content.quantidadeFinalMargemCartao

    const campanha: CAMPANHA['register'] = {
      id: response?.data?.content.id,
      descricao: response?.data?.content.descricao,
      cliente: response?.data?.content.cliente ?? false,
      basePropria: response?.data?.content.basePropria ?? false,
      carteira:
        response?.data?.content.carteira ??
        response?.data?.content.consignadoRapido ??
        false,
      discadorAutomatico:
        response?.data?.content.discadorAutomatico ??
        (response?.data?.content.tipoCampanhaCrmId ===
          'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' ||
          response?.data?.content.tipoCampanhaCrmId ===
            'd38a3f12-927f-4936-8eee-6552ce2ed81e'),
      uploadArquivoBase64: response?.data?.content.uploadArquivoBase64 ?? null,
      empresasGrupo: response?.data?.content.lojaId
        ? [response.data.content.lojaId]
        : [],
      tipoOperacaoId: response?.data?.content.tipoOperacaoId,
      tipoCampanhaCrmId: response?.data?.content.tipoCampanhaCrmId,
      dataInicioCampanha: maskDate(
        response?.data?.content.dataInicioCampanha,
        'YYYY-MM-DD'
      ),
      dataFimCampanha: maskDate(
        response?.data?.content.dataFimCampanha,
        'YYYY-MM-DD'
      ),
      quantidadeClientes: response?.data?.content.quantidadeClientes,
      ufCampanha:
        Array.isArray(response?.data?.content.ufs) &&
        response?.data?.content.ufs.length > 0
          ? [...response.data.content.ufs]
          : [],
      cidadeCampanha:
        Array.isArray(response?.data?.content.cidades) &&
        response?.data?.content.cidades.length > 0
          ? [...response.data.content.cidades]
          : [],
      redudancia: response?.data?.content.redudancia,
      ignorarClientesCarteira:
        response?.data?.content.ignorarClientesCarteira ?? false,
      ignorarEnviadosElevenLabs:
        response?.data?.content.ignorarEnviadosElevenLabs ?? false,
      indisponibilidade: response?.data?.content.indisponibilidade,
      naoPerturbe: response?.data?.content.naoPerturbe,
      telefone: response?.data?.content.telefone,
      idadeInicial: response?.data?.content.idadeInicial,
      idadeFinal: response?.data?.content.idadeFinal,
      formaRecebimentoBeneficio:
        response?.data?.content.formaRecebimentoBeneficio,
      cartaoRmc:
        response?.data?.content.cartaoRmc ?? response?.data?.content.cartaoInss,
      cartaoRcc: response?.data?.content.cartaoRcc,
      bancosCartaoRc:
        response?.data?.content.bancosCartaoRc ||
        response?.data?.content.bancosCartao ||
        [],
      despachoAnoInicial: maskDate(
        response?.data?.content.despachoAnoInicial,
        'YYYY-MM-DD'
      ),
      despachoAnoFinal: maskDate(
        response?.data?.content.despachoAnoFinal,
        'YYYY-MM-DD'
      ),
      especies: response?.data?.content.especies || [],
      bancosPagadoresConsiderarCampanha:
        response?.data?.content.bancosPagadoresConsiderar || [],
      quantidadeEmprestimo: response?.data?.content.quantidadeEmprestimo,
      quantidadeInicialEmprestimo:
        response?.data?.content.quantidadeInicialEmprestimo,
      quantidadeFinalEmprestimo:
        response?.data?.content.quantidadeFinalEmprestimo,
      salario: response?.data?.content.salario,
      quantidadeInicialSalario: maskMoneyReq(
        response?.data?.content.quantidadeInicialSalario
      ),
      quantidadeFinalSalario: maskMoneyReq(
        response?.data?.content.quantidadeFinalSalario
      ),
      margemLivre: response?.data?.content.margemLivre,
      quantidadeInicialMargem: maskMoneyReq(
        response?.data?.content.quantidadeInicialMargem
      ),
      quantidadeFinalMargem: maskMoneyReq(
        response?.data?.content.quantidadeFinalMargem
      ),
      margemCartao:
        response?.data?.content.margemCartao ??
        response?.data?.content.limiteCartao,
      quantidadeInicialMargemCartao: maskMoneyReq(
        response?.data?.content.quantidadeInicialMargemCartao ??
          quantidadeInicialLimiteCartao
      ),
      quantidadeFinalMargemCartao: maskMoneyReq(
        response?.data?.content.quantidadeFinalMargemCartao ??
          quantidadeFinalLimiteCartao
      ),
      limiteCartao:
        response?.data?.content.limiteCartao ??
        response?.data?.content.margemCartao,
      quantidadeInicialLimiteCartao: maskMoneyReq(
        quantidadeInicialLimiteCartao
      ),
      quantidadeFinalLimiteCartao: maskMoneyReq(quantidadeFinalLimiteCartao),
      instituicaoFinanceiraCampanha:
        response?.data?.content.instituicaoFinanceira || [],
      parcelas: response?.data?.content.parcelas,
      quantidadeInicialParcelas:
        response?.data?.content.quantidadeInicialParcelas,
      quantidadeFinalParcelas: response?.data?.content.quantidadeFinalParcelas,
      valorParcela: response?.data?.content.valorParcela,
      quantidadeInicialValorParcela: maskMoneyReq(
        response?.data?.content.quantidadeInicialValorParcela
      ),
      quantidadeFinalValorParcela: maskMoneyReq(
        response?.data?.content.quantidadeFinalValorParcela
      ),
      representanteLegal: response?.data?.content.representanteLegal,
      campanhaCrmParametrosDiscador: {
        horaInicio:
          response.data?.content.campanhaCrmParametrosDiscador.horaInicio,
        horaFim: response.data?.content.campanhaCrmParametrosDiscador.horaFim,
        tocarAudio:
          response.data?.content.campanhaCrmParametrosDiscador.tocarAudio,
        habilitarAmd:
          response.data?.content.campanhaCrmParametrosDiscador.habilitarAmd,
        maxDiscagensSimultaneas:
          response.data?.content.campanhaCrmParametrosDiscador
            .maxDiscagensSimultaneas,
        fila: response.data?.content.campanhaCrmParametrosDiscador.fila,
        maxDiscagensEspera:
          response.data?.content.campanhaCrmParametrosDiscador
            .maxDiscagensEspera,
        pergunta: response.data?.content.campanhaCrmParametrosDiscador.pergunta,
        esperaNaFila:
          response.data?.content.campanhaCrmParametrosDiscador.esperaNaFila,
        discagemPorAgente:
          response.data?.content.campanhaCrmParametrosDiscador
            .discagemPorAgente,
        pabxipCategoriaSaida:
          response.data?.content.campanhaCrmParametrosDiscador
            .pabxipCategoriaSaida
      },
      agendarPara:
        maskDate(
          campanhaCrmParametrosElevenLabs?.agendarPara ??
            response?.data?.content.agendarPara ??
            '',
          'YYYY-MM-DD'
        ) || null,
      agentesElevenLabsId:
        campanhaCrmParametrosElevenLabs?.agenteId ??
        response?.data?.content.agentesElevenLabsId ??
        null,
      numerosOrigemElevenLabsId:
        campanhaCrmParametrosElevenLabs?.numeroOrigemId ??
        response?.data?.content.numerosOrigemElevenLabsId ??
        null,
      variaveis:
        campanhaCrmParametrosElevenLabs?.variaveisDinamicas ??
        response?.data?.content.variaveis ??
        null,
      tempoLimiteToque:
        campanhaCrmParametrosElevenLabs?.tempoLimiteToque ?? null,
      limiteConcorrencia:
        campanhaCrmParametrosElevenLabs?.limiteConcorrencia ?? null
    }
    yield put(campanhaActions.setRegister(campanha))
    yield put(campanhaActions.setActiveTab('register'))
    yield put(campanhaActions.setVisualizacao(true))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* editarCampanha({ id }: GenericProps): SagaIterator {
  yield put(loadingActions.setLoading(true))
  try {
    const response = yield call(campanhaController.getCampanha, id)
    const campanhaCrmParametrosElevenLabs =
      response?.data?.content.campanhaCrmParametrosElevenLabs

    const quantidadeInicialLimiteCartao =
      response?.data?.content.quantidadeInicialLimiteCartao ??
      response?.data?.content.quantidadeInicialMargemCartao

    const quantidadeFinalLimiteCartao =
      response?.data?.content.quantidadeFinalLimiteCartao ??
      response?.data?.content.quantidadeFinalMargemCartao

    const campanha: CAMPANHA['register'] = {
      id: response?.data?.content.id,
      descricao: response?.data?.content.descricao,
      cliente: response?.data?.content.cliente ?? false,
      basePropria: response?.data?.content.basePropria ?? false,
      carteira:
        response?.data?.content.carteira ??
        response?.data?.content.consignadoRapido ??
        false,
      discadorAutomatico:
        response?.data?.content.discadorAutomatico ??
        (response?.data?.content.tipoCampanhaCrmId ===
          'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' ||
          response?.data?.content.tipoCampanhaCrmId ===
            'd38a3f12-927f-4936-8eee-6552ce2ed81e'),
      uploadArquivoBase64: response?.data?.content.uploadArquivoBase64 ?? null,
      empresasGrupo: response?.data?.content.lojaId
        ? [response?.data?.content.lojaId]
        : [],
      tipoOperacaoId: response?.data?.content.tipoOperacaoId,
      dataInicioCampanha: maskDate(
        response?.data?.content.dataInicioCampanha,
        'YYYY-MM-DD'
      ),
      dataFimCampanha: maskDate(
        response?.data?.content.dataFimCampanha,
        'YYYY-MM-DD'
      ),
      quantidadeClientes: response?.data?.content.quantidadeClientes,
      ufCampanha:
        Array.isArray(response?.data?.content.ufs) &&
        response?.data?.content.ufs.length > 0
          ? [...response.data.content.ufs]
          : [],
      cidadeCampanha:
        Array.isArray(response?.data?.content.cidades) &&
        response?.data?.content.cidades.length > 0
          ? [...response.data.content.cidades]
          : [],
      redudancia: response?.data?.content.redudancia,
      ignorarClientesCarteira:
        response?.data?.content.ignorarClientesCarteira ?? false,
      ignorarEnviadosElevenLabs:
        response?.data?.content.ignorarEnviadosElevenLabs ?? false,
      indisponibilidade: response?.data?.content.indisponibilidade,
      naoPerturbe: response?.data?.content.naoPerturbe,
      telefone: response?.data?.content.telefone,
      idadeInicial: response?.data?.content.idadeInicial,
      idadeFinal: response?.data?.content.idadeFinal,
      formaRecebimentoBeneficio:
        response?.data?.content.formaRecebimentoBeneficio,
      cartaoRmc:
        response?.data?.content.cartaoRmc ?? response?.data?.content.cartaoInss,
      cartaoRcc: response?.data?.content.cartaoRcc,
      bancosCartaoRc:
        response?.data?.content.bancosCartaoRc ||
        response?.data?.content.bancosCartao ||
        [],
      despachoAnoInicial: maskDate(
        response?.data?.content.despachoAnoInicial,
        'YYYY-MM-DD'
      ),
      despachoAnoFinal: maskDate(
        response?.data?.content.despachoAnoFinal,
        'YYYY-MM-DD'
      ),
      especies: response?.data?.content.especies || [],
      bancosPagadoresConsiderarCampanha:
        response?.data?.content.bancosPagadoresConsiderar || [],
      quantidadeEmprestimo: response?.data?.content.quantidadeEmprestimo,
      quantidadeInicialEmprestimo:
        response?.data?.content.quantidadeInicialEmprestimo,
      quantidadeFinalEmprestimo:
        response?.data?.content.quantidadeFinalEmprestimo,
      salario: response?.data?.content.salario,
      quantidadeInicialSalario: maskMoneyReq(
        response?.data?.content.quantidadeInicialSalario
      ),
      quantidadeFinalSalario: maskMoneyReq(
        response?.data?.content.quantidadeFinalSalario
      ),
      margemLivre: response?.data?.content.margemLivre,
      quantidadeInicialMargem: maskMoneyReq(
        response?.data?.content.quantidadeInicialMargem
      ),
      quantidadeFinalMargem: maskMoneyReq(
        response?.data?.content.quantidadeFinalMargem
      ),
      margemCartao:
        response?.data?.content.margemCartao ??
        response?.data?.content.limiteCartao,
      quantidadeInicialMargemCartao: maskMoneyReq(
        response?.data?.content.quantidadeInicialMargemCartao ??
          quantidadeInicialLimiteCartao
      ),
      quantidadeFinalMargemCartao: maskMoneyReq(
        response?.data?.content.quantidadeFinalMargemCartao ??
          quantidadeFinalLimiteCartao
      ),
      limiteCartao:
        response?.data?.content.limiteCartao ??
        response?.data?.content.margemCartao,
      quantidadeInicialLimiteCartao: maskMoneyReq(
        quantidadeInicialLimiteCartao
      ),
      quantidadeFinalLimiteCartao: maskMoneyReq(quantidadeFinalLimiteCartao),
      instituicaoFinanceiraCampanha:
        response?.data?.content.instituicaoFinanceira || [],
      parcelas: response?.data?.content.parcelas,
      quantidadeInicialParcelas:
        response?.data?.content.quantidadeInicialParcelas,
      quantidadeFinalParcelas: response?.data?.content.quantidadeFinalParcelas,
      valorParcela: response?.data?.content.valorParcela,
      quantidadeInicialValorParcela: maskMoneyReq(
        response?.data?.content.quantidadeInicialValorParcela
      ),
      quantidadeFinalValorParcela: maskMoneyReq(
        response?.data?.content.quantidadeFinalValorParcela
      ),
      representanteLegal: response.data?.content.representanteLegal,
      tipoCampanhaCrmId: response.data?.content.tipoCampanhaCrmId,
      campanhaCrmParametrosDiscador: {
        horaInicio:
          response.data?.content.campanhaCrmParametrosDiscador.horaInicio,
        horaFim: response.data?.content.campanhaCrmParametrosDiscador.horaFim,
        tocarAudio:
          response.data?.content.campanhaCrmParametrosDiscador.tocarAudio,
        habilitarAmd:
          response.data?.content.campanhaCrmParametrosDiscador.habilitarAmd,
        maxDiscagensSimultaneas:
          response.data?.content.campanhaCrmParametrosDiscador
            .maxDiscagensSimultaneas,
        fila: response.data?.content.campanhaCrmParametrosDiscador.fila,
        maxDiscagensEspera:
          response.data?.content.campanhaCrmParametrosDiscador
            .maxDiscagensEspera,
        pergunta: response.data?.content.campanhaCrmParametrosDiscador.pergunta,
        esperaNaFila:
          response.data?.content.campanhaCrmParametrosDiscador.esperaNaFila,
        discagemPorAgente:
          response.data?.content.campanhaCrmParametrosDiscador
            .discagemPorAgente,
        pabxipCategoriaSaida:
          response.data?.content.campanhaCrmParametrosDiscador
            .pabxipCategoriaSaida
      },
      agendarPara:
        maskDate(
          campanhaCrmParametrosElevenLabs?.agendarPara ??
            response?.data?.content.agendarPara ??
            '',
          'YYYY-MM-DD'
        ) || null,
      agentesElevenLabsId:
        campanhaCrmParametrosElevenLabs?.agenteId ??
        response?.data?.content.agentesElevenLabsId ??
        null,
      numerosOrigemElevenLabsId:
        campanhaCrmParametrosElevenLabs?.numeroOrigemId ??
        response?.data?.content.numerosOrigemElevenLabsId ??
        null,
      variaveis:
        campanhaCrmParametrosElevenLabs?.variaveisDinamicas ??
        response?.data?.content.variaveis ??
        null,
      tempoLimiteToque:
        campanhaCrmParametrosElevenLabs?.tempoLimiteToque ?? null,
      limiteConcorrencia:
        campanhaCrmParametrosElevenLabs?.limiteConcorrencia ?? null
    }
    const isCampanhaElevenLabs =
      campanha.tipoCampanhaCrmId === '58dbb40f-ff45-4a29-8375-fcc9bb726d1f'

    yield put(campanhaActions.setRegister(campanha))
    yield put(campanhaActions.setActiveTab('register'))
    yield put(campanhaActions.setVisualizacao(isCampanhaElevenLabs))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* criarCampanha(): SagaIterator {
  const { register, optionsSelect }: CAMPANHA = yield select(
    (state: ApplicationState) => state.campanha
  )
  try {
    yield put(loadingActions.setLoading(true))
    const data: ParametrizacaoCampanhaReq = {
      id: register.id,
      codigo: register.codigo,
      descricao: register.descricao,
      cliente: register.cliente,
      carteira: register.carteira,
      basePropria: register.basePropria,
      consignadoRapido: register.carteira,
      discadorAutomatico:
        register.tipoCampanhaCrmId === 'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' ||
        register.tipoCampanhaCrmId === 'd38a3f12-927f-4936-8eee-6552ce2ed81e'
          ? true
          : register.discadorAutomatico,
      uploadArquivoBase64: register.uploadArquivoBase64,
      lojas: register.empresasGrupo.filter((lojaGrupo) => {
        return optionsSelect.empresasGrupos.some(
          (x) =>
            x.idGrupoELoja === lojaGrupo && x.descricao?.startsWith('Loja -')
        )
      }),
      grupos: register.empresasGrupo.filter((lojaGrupo) => {
        return optionsSelect.empresasGrupos.some(
          (x) =>
            x.idGrupoELoja === lojaGrupo && x.descricao?.startsWith('Grupo -')
        )
      }),
      tipoOperacaoId: register.tipoOperacaoId,
      dataInicioCampanha: register.dataInicioCampanha,
      dataFimCampanha: register.dataFimCampanha,
      quantidadeClientes: register.quantidadeClientes,
      ufCampanha: register.ufCampanha,
      cidadeCampanha: register.cidadeCampanha,
      redudancia: register.redudancia,
      ignorarClientesCarteira: register.cliente
        ? true
        : register.ignorarClientesCarteira,
      ignorarEnviadosElevenLabs: register.ignorarEnviadosElevenLabs,
      indisponibilidade: register.indisponibilidade,
      naoPerturbe: register.naoPerturbe,
      naoPertube: register.naoPerturbe,
      telefone: register.telefone,
      idadeInicial: register.idadeInicial,
      idadeFinal: register.idadeFinal,
      formaRecebimentoBeneficio: register.formaRecebimentoBeneficio,
      cartaoRmc: register.cartaoRmc,
      cartaoRcc: register.cartaoRcc,
      bancosCartaoRc: register.bancosCartaoRc,
      despachoAnoInicial: new Date(
        Date.parse(register.despachoAnoInicial ?? '')
      ),
      especies: register.especies,
      despachoAnoFinal: new Date(Date.parse(register.despachoAnoFinal ?? '')),
      bancosPagadoresConsiderarCampanha:
        register.bancosPagadoresConsiderarCampanha,
      quantidadeEmprestimo: register.quantidadeEmprestimo,
      quantidadeInicialEmprestimo: register.quantidadeInicialEmprestimo,
      quantidadeFinalEmprestimo: register.quantidadeFinalEmprestimo,
      salario: register.salario,
      quantidadeInicialSalario: register.quantidadeInicialSalario
        ? Number(removeMaskMoney(register.quantidadeInicialSalario))
        : null,
      quantidadeFinalSalario: register.quantidadeFinalSalario
        ? Number(removeMaskMoney(register.quantidadeFinalSalario))
        : null,
      margemLivre: register.margemLivre,
      quantidadeInicialMargem: register.quantidadeInicialMargem
        ? Number(removeMaskMoney(register.quantidadeInicialMargem))
        : null,
      quantidadeFinalMargem: register.quantidadeFinalMargem
        ? Number(removeMaskMoney(register.quantidadeFinalMargem))
        : null,
      margemCartao: register.margemCartao,
      quantidadeInicialMargemCartao: register.quantidadeInicialMargemCartao
        ? Number(removeMaskMoney(register.quantidadeInicialMargemCartao))
        : null,
      quantidadeFinalMargemCartao: register.quantidadeFinalMargemCartao
        ? Number(removeMaskMoney(register.quantidadeFinalMargemCartao))
        : null,
      limiteCartao: register.limiteCartao || register.margemCartao,
      quantidadeInicialLimiteCartao: register.quantidadeInicialLimiteCartao
        ? Number(removeMaskMoney(register.quantidadeInicialLimiteCartao))
        : register.quantidadeInicialMargemCartao
          ? Number(removeMaskMoney(register.quantidadeInicialMargemCartao))
          : null,
      quantidadeFinalLimiteCartao: register.quantidadeFinalLimiteCartao
        ? Number(removeMaskMoney(register.quantidadeFinalLimiteCartao))
        : register.quantidadeFinalMargemCartao
          ? Number(removeMaskMoney(register.quantidadeFinalMargemCartao))
          : null,
      instituicaoFinanceiraCampanha: register.instituicaoFinanceiraCampanha,
      parcelas: register.parcelas,
      quantidadeInicialParcelas: register.quantidadeInicialParcelas,
      quantidadeFinalParcelas: register.quantidadeFinalParcelas,
      valorParcela: register.valorParcela,
      quantidadeInicialValorParcela: register.quantidadeInicialValorParcela
        ? Number(removeMaskMoney(register.quantidadeInicialValorParcela))
        : null,
      quantidadeFinalValorParcela: register.quantidadeFinalValorParcela
        ? Number(removeMaskMoney(register.quantidadeFinalValorParcela))
        : null,
      representanteLegal: register.representanteLegal,
      tipoCampanhaCrmId: register.tipoCampanhaCrmId,
      campanhaCrmParametrosDiscador: {
        horaInicio: register.campanhaCrmParametrosDiscador.horaInicio,
        horaFim: register.campanhaCrmParametrosDiscador.horaFim,
        tocarAudio: register.campanhaCrmParametrosDiscador.tocarAudio,
        habilitarAmd: register.campanhaCrmParametrosDiscador.habilitarAmd,
        maxDiscagensSimultaneas:
          register.campanhaCrmParametrosDiscador.maxDiscagensSimultaneas,
        fila: register.campanhaCrmParametrosDiscador.fila,
        maxDiscagensEspera:
          register.campanhaCrmParametrosDiscador.maxDiscagensEspera,
        pergunta: register.campanhaCrmParametrosDiscador.pergunta,
        esperaNaFila: register.campanhaCrmParametrosDiscador.esperaNaFila,
        discagemPorAgente:
          register.campanhaCrmParametrosDiscador.discagemPorAgente,
        pabxipCategoriaSaida: resolveCategoriaSaida(register, optionsSelect)
      },
      ...(register.tipoCampanhaCrmId === '58dbb40f-ff45-4a29-8375-fcc9bb726d1f'
        ? {
            campanhaCrmParametrosElevenLabs: {
              agenteId: register.agentesElevenLabsId,
              numeroOrigemId: register.numerosOrigemElevenLabsId,
              agendarPara: register.agendarPara,
              variaveisDinamicas: register.variaveis,
              tempoLimiteToque: register.tempoLimiteToque,
              limiteConcorrencia: register.limiteConcorrencia
            }
          }
        : {})
    }
    yield call(campanhaController.criarCampanha, data)
    // Temporariamente desativado.
    // if (!data.basePropria) {
    //   yield put(
    //     campanhaActions.abastecimentoClientesSaga({
    //       dataFimCampanha: data.dataFimCampanha,
    //       dataInicioCampanha: data.dataInicioCampanha,
    //       descricaoCampanha: data.descricao,
    //       quantidadeClientes: data.quantidadeClientes,
    //       lojas: register.empresasGrupo.filter((lojaGrupo) => {
    //         return optionsSelect.empresasGrupos.some(
    //           (x) =>
    //             x.idGrupoELoja === lojaGrupo &&
    //             x.descricao?.startsWith('Loja -')
    //         )
    //       }),
    //       grupos: register.empresasGrupo.filter((lojaGrupo) => {
    //         return optionsSelect.empresasGrupos.some(
    //           (x) =>
    //             x.idGrupoELoja === lojaGrupo &&
    //             x.descricao?.startsWith('Grupo -')
    //         )
    //       })
    //     })
    //   )
    // }
    yield put(campanhaActions.toggleModalAprovacaoClientes(false))
    yield put(campanhaActions.cleanPreviaClientesPorLoja())
    yield put(campanhaActions.cleanRegister())
    yield put(
      campanhaActions.setPaginacao({
        page: 0,
        pageSize: 10,
        totalRegistros: 0
      })
    )
    yield put(campanhaActions.cleanPesquisa())
    yield put(campanhaActions.getAllCampanhasSaga())
    yield put(campanhaActions.setActiveTabRegister(1))
    yield put(campanhaActions.setActiveTab('search'))
    toast.success('Campanha salva com sucesso!')
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* atualizarCampanha(): SagaIterator {
  const { register, optionsSelect }: CAMPANHA = yield select(
    (state: ApplicationState) => state.campanha
  )
  try {
    yield put(loadingActions.setLoading(true))
    const data: ParametrizacaoCampanhaReq = {
      id: register.id,
      codigo: register.codigo,
      descricao: register.descricao,
      cliente: register.cliente,
      carteira: register.carteira,
      basePropria: register.basePropria,
      consignadoRapido: register.carteira,
      discadorAutomatico:
        register.tipoCampanhaCrmId === 'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' ||
        register.tipoCampanhaCrmId === 'd38a3f12-927f-4936-8eee-6552ce2ed81e'
          ? true
          : register.discadorAutomatico,
      uploadArquivoBase64: register.uploadArquivoBase64,
      lojas: register.empresasGrupo.filter((lojaGrupo) => {
        return optionsSelect.empresasGrupos.some(
          (x) =>
            x.idGrupoELoja === lojaGrupo && x.descricao?.startsWith('Loja -')
        )
      }),
      grupos: register.empresasGrupo.filter((lojaGrupo) => {
        return optionsSelect.empresasGrupos.some(
          (x) =>
            x.idGrupoELoja === lojaGrupo && x.descricao?.startsWith('Grupo -')
        )
      }),
      tipoOperacaoId: register.tipoOperacaoId,
      dataInicioCampanha: register.dataInicioCampanha,
      dataFimCampanha: register.dataFimCampanha,
      quantidadeClientes: register.quantidadeClientes,
      ufCampanha: register.ufCampanha,
      cidadeCampanha: register.cidadeCampanha,
      redudancia: register.redudancia,
      ignorarClientesCarteira: register.cliente
        ? true
        : register.ignorarClientesCarteira,
      ignorarEnviadosElevenLabs: register.ignorarEnviadosElevenLabs,
      indisponibilidade: register.indisponibilidade,
      naoPerturbe: register.naoPerturbe,
      naoPertube: register.naoPerturbe,
      telefone: register.telefone,
      idadeInicial: register.idadeInicial,
      idadeFinal: register.idadeFinal,
      formaRecebimentoBeneficio: register.formaRecebimentoBeneficio,
      cartaoRmc: register.cartaoRmc,
      cartaoRcc: register.cartaoRcc,
      bancosCartaoRc: register.bancosCartaoRc,
      despachoAnoInicial: new Date(
        Date.parse(register.despachoAnoInicial ?? '')
      ),
      despachoAnoFinal: new Date(Date.parse(register.despachoAnoFinal ?? '')),
      especies: register.especies,
      bancosPagadoresConsiderarCampanha:
        register.bancosPagadoresConsiderarCampanha,
      quantidadeEmprestimo: register.quantidadeEmprestimo,
      quantidadeInicialEmprestimo: register.quantidadeInicialEmprestimo,
      quantidadeFinalEmprestimo: register.quantidadeFinalEmprestimo,
      salario: register.salario,
      quantidadeInicialSalario: register.quantidadeInicialSalario
        ? Number(removeMaskMoney(register.quantidadeInicialSalario))
        : null,
      quantidadeFinalSalario: register.quantidadeFinalSalario
        ? Number(removeMaskMoney(register.quantidadeFinalSalario))
        : null,
      margemLivre: register.margemLivre,
      quantidadeInicialMargem: register.quantidadeInicialMargem
        ? Number(removeMaskMoney(register.quantidadeInicialMargem))
        : null,
      quantidadeFinalMargem: register.quantidadeFinalMargem
        ? Number(removeMaskMoney(register.quantidadeFinalMargem))
        : null,
      margemCartao: register.margemCartao,
      quantidadeInicialMargemCartao: register.quantidadeInicialMargemCartao
        ? Number(removeMaskMoney(register.quantidadeInicialMargemCartao))
        : null,
      quantidadeFinalMargemCartao: register.quantidadeFinalMargemCartao
        ? Number(removeMaskMoney(register.quantidadeFinalMargemCartao))
        : null,
      limiteCartao: register.limiteCartao || register.margemCartao,
      quantidadeInicialLimiteCartao: register.quantidadeInicialLimiteCartao
        ? Number(removeMaskMoney(register.quantidadeInicialLimiteCartao))
        : register.quantidadeInicialMargemCartao
          ? Number(removeMaskMoney(register.quantidadeInicialMargemCartao))
          : null,
      quantidadeFinalLimiteCartao: register.quantidadeFinalLimiteCartao
        ? Number(removeMaskMoney(register.quantidadeFinalLimiteCartao))
        : register.quantidadeFinalMargemCartao
          ? Number(removeMaskMoney(register.quantidadeFinalMargemCartao))
          : null,
      instituicaoFinanceiraCampanha: register.instituicaoFinanceiraCampanha,
      parcelas: register.parcelas,
      quantidadeInicialParcelas: register.quantidadeInicialParcelas,
      quantidadeFinalParcelas: register.quantidadeFinalParcelas,
      valorParcela: register.valorParcela,
      quantidadeInicialValorParcela: register.quantidadeInicialValorParcela
        ? Number(removeMaskMoney(register.quantidadeInicialValorParcela))
        : null,
      quantidadeFinalValorParcela: register.quantidadeFinalValorParcela
        ? Number(removeMaskMoney(register.quantidadeFinalValorParcela))
        : null,
      representanteLegal: register.representanteLegal,
      tipoCampanhaCrmId: register.tipoCampanhaCrmId,
      campanhaCrmParametrosDiscador: {
        horaInicio: register.campanhaCrmParametrosDiscador.horaInicio,
        horaFim: register.campanhaCrmParametrosDiscador.horaFim,
        tocarAudio: register.campanhaCrmParametrosDiscador.tocarAudio,
        habilitarAmd: register.campanhaCrmParametrosDiscador.habilitarAmd,
        maxDiscagensSimultaneas:
          register.campanhaCrmParametrosDiscador.maxDiscagensSimultaneas,
        fila: register.campanhaCrmParametrosDiscador.fila,
        maxDiscagensEspera:
          register.campanhaCrmParametrosDiscador.maxDiscagensEspera,
        pergunta: register.campanhaCrmParametrosDiscador.pergunta,
        esperaNaFila: register.campanhaCrmParametrosDiscador.esperaNaFila,
        discagemPorAgente:
          register.campanhaCrmParametrosDiscador.discagemPorAgente,
        pabxipCategoriaSaida: resolveCategoriaSaida(register, optionsSelect)
      },
      ...(register.tipoCampanhaCrmId === '58dbb40f-ff45-4a29-8375-fcc9bb726d1f'
        ? {
            campanhaCrmParametrosElevenLabs: {
              agenteId: register.agentesElevenLabsId,
              numeroOrigemId: register.numerosOrigemElevenLabsId,
              agendarPara: register.agendarPara,
              variaveisDinamicas: register.variaveis,
              tempoLimiteToque: register.tempoLimiteToque,
              limiteConcorrencia: register.limiteConcorrencia
            }
          }
        : {})
    }
    yield call(campanhaController.atualizarCampanha, data)
    // Temporariamente desativado.
    // if (!data.basePropria) {
    //   yield put(
    //     campanhaActions.reabastecimentoClientesSaga({
    //       campanhaCrmId: data.id ?? '',
    //       quantidadeClientes: data.quantidadeClientes
    //     })
    //   )
    // }
    yield put(campanhaActions.toggleModalAprovacaoClientes(false))
    yield put(campanhaActions.cleanPreviaClientesPorLoja())
    yield put(campanhaActions.cleanRegister())
    yield put(
      campanhaActions.setPaginacao({
        page: 0,
        pageSize: 10,
        totalRegistros: 0
      })
    )
    yield put(campanhaActions.cleanPesquisa())
    yield put(campanhaActions.getAllCampanhasSaga())
    yield put(campanhaActions.setActiveTabRegister(1))
    yield put(campanhaActions.setActiveTab('search'))
    toast.success('Campanha atualizada com sucesso!')
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* deletarCampanha(): SagaIterator {
  const { itemSelectedId, campanhasSelectedIds }: CAMPANHA = yield select(
    (state: ApplicationState) => state.campanha
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(campanhaController.deletarCampanha, itemSelectedId)

    const newCampanhasSelectedIds = campanhasSelectedIds.filter(
      (id) => id !== itemSelectedId
    )
    yield put(campanhaActions.setCampanhasSelectedIds(newCampanhasSelectedIds))

    yield put(campanhaActions.getAllCampanhasSaga())
    toast.success('Campanha excluída com sucesso!')
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* deletarCampanhasSelecionadas(): SagaIterator {
  const { campanhasSelectedIds, campanhas }: CAMPANHA = yield select(
    (state: ApplicationState) => state.campanha
  )
  try {
    const possuiCampanhaElevenLabs = campanhas.some(
      (campanha) =>
        campanhasSelectedIds.includes(campanha.id) &&
        campanha.descricaoTipoCampanha?.toUpperCase() === 'ELEVENLABS'
    )

    if (possuiCampanhaElevenLabs) {
      toast.error(
        'Campanhas do tipo ElevenLabs não podem ser excluídas. Remova-as da seleção para continuar.'
      )
      yield put(campanhaActions.toggleModalExclusaoSelecionadosDispatch(false))
      return
    }

    yield put(loadingActions.setLoading(true))
    yield call(campanhaController.deletarCampanhas, campanhasSelectedIds)
    yield put(campanhaActions.cleanCampanhasSelectedIds())
    yield put(campanhaActions.getAllCampanhasSaga())
    toast.success('Campanhas excluídas com sucesso!')
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getCidades(): SagaIterator {
  const { register }: CAMPANHA = yield select(
    (state: ApplicationState) => state.campanha
  )
  try {
    let response: Cidade[] = []
    if (register.ufCampanha && register.ufCampanha.length > 0) {
      const getCidades = function* (): Generator<CallEffect> {
        for (const uf of register.ufCampanha) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const simulacao: any = yield call(campanhaController.getCidades, uf)
          response = response.concat(simulacao?.data?.content)
        }
      }
      yield call(getCidades)
      yield put(campanhaActions.setOptionsSelect(response, 'cidades'))
    } else {
      yield put(campanhaActions.setOptionsSelect([], 'cidades'))
    }
  } catch (error) {
    handlerError(error)
  }
}
function* getGruposLojas(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))

    const { register }: CAMPANHA = yield select(
      (state: ApplicationState) => state.campanha
    )

    if (
      !register.tipoCampanhaCrmId ||
      register.tipoCampanhaCrmId.trim() === ''
    ) {
      toast.error('É necessário cadastrar o código de empresa para esta loja')
      return
    }

    const response = yield call(
      campanhaController.getGruposLojas,
      register.tipoCampanhaCrmId
    )

    yield put(
      campanhaActions.setOptionsSelect(
        response?.data?.content || [],
        'empresasGrupos'
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getEstados(): SagaIterator {
  try {
    const response = yield call(campanhaController.getEstados)
    yield put(campanhaActions.setOptionsSelect(response?.data?.content, 'ufs'))
  } catch (error) {
    handlerError(error)
  }
}

function* getGeneros(): SagaIterator {
  try {
    const response = yield call(campanhaController.getGeneros)
    yield put(
      campanhaActions.setOptionsSelect(response?.data?.content, 'generos')
    )
  } catch (error) {
    handlerError(error)
  }
}

function* getTiposOperacao(): SagaIterator {
  try {
    const response = yield call(campanhaController.getTiposOperacao)
    yield put(
      campanhaActions.setOptionsSelect(response?.data?.content, 'tiposOperacao')
    )
  } catch (error) {
    handlerError(error)
  }
}

function* getBancos(): SagaIterator {
  try {
    const response = yield call(campanhaController.getBancos)
    yield put(
      campanhaActions.setOptionsSelect(response?.data?.content, 'bancos')
    )
  } catch (error) {
    handlerError(error)
  }
}

function* getBancosCartao(): SagaIterator {
  try {
    const response = yield call(campanhaController.getBancosCartao)
    yield put(
      campanhaActions.setOptionsSelect(response?.data?.content, 'bancosCartao')
    )
  } catch (error) {
    handlerError(error)
  }
}

interface AlterarStatusCampanhaProps extends GenericProps {
  campanhaCrmId: string
  ativo: boolean
}

function* alterarStatusCampanha({
  campanhaCrmId,
  ativo
}: AlterarStatusCampanhaProps): SagaIterator {
  try {
    yield call(campanhaController.alterarStatusCampanha, campanhaCrmId, ativo)
    yield put(campanhaActions.setStatusCampanhaGrid(campanhaCrmId, ativo))
    toast.success('Ação realizada com sucesso!')
  } catch (error) {
    handlerError(error)
  }
}

/*
interface AbastecimentoClientesProps extends GenericProps {
  value: AbastecimentoCampanhaDto
}
function* abastecimentoClientes({
  value
}: AbastecimentoClientesProps): SagaIterator {
  try {
    yield call(campanhaController.abastecimentoClientes, value)
    toast.success(
      'Abastecimento de clientes da campanha realizado com sucesso!'
    )
    yield put(campanhaActions.getAllCampanhasSaga())
  } catch (error) {
    handlerError(
      error,
      'Não foi possível realizar o abastecimento da campanha. Por favor, volte mais tarde para realizar um novo abastecimento.'
    )
  }
}

interface ReabastecimentoClientesProps extends GenericProps {
  value: ReabastecimentoCampanhaDto
}
function* reabastecimentoClientes({
  value
}: ReabastecimentoClientesProps): SagaIterator {
  try {
    yield call(campanhaController.reabastecimentoClientes, value)
    toast.success(
      'Reabastecimento de clientes da campanha realizado com sucesso!'
    )
    yield put(campanhaActions.getAllCampanhasSaga())
  } catch (error) {
    handlerError(
      error,
      'Não foi possível realizar o reabastecimento da campanha. Por favor, volte mais tarde para realizar um novo reabastecimento.'
    )
  }
}
*/

function* getPreviaClientesPorLoja(): SagaIterator {
  const { register, optionsSelect }: CAMPANHA = yield select(
    (state: ApplicationState) => state.campanha
  )

  try {
    yield put(loadingActions.setLoading(true))

    const cidadesCampanhaPrevia = register.cidadeCampanha.map((cidadeId) => {
      const cidadeSelecionada = optionsSelect.cidades.find(
        (cidade) => cidade.id === cidadeId
      )

      return cidadeSelecionada?.nome || cidadeId
    })

    const data: ParametrizacaoCampanhaReq = {
      id: register.id,
      codigo: register.codigo,
      descricao: register.descricao,
      cliente: register.cliente,
      carteira: register.carteira,
      basePropria: register.basePropria,
      consignadoRapido: register.carteira,
      discadorAutomatico:
        register.tipoCampanhaCrmId === 'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' ||
        register.tipoCampanhaCrmId === 'd38a3f12-927f-4936-8eee-6552ce2ed81e'
          ? true
          : register.discadorAutomatico,
      uploadArquivoBase64: register.uploadArquivoBase64,
      lojas: register.empresasGrupo.filter((lojaGrupo) => {
        return optionsSelect.empresasGrupos.some(
          (x) =>
            x.idGrupoELoja === lojaGrupo && x.descricao?.startsWith('Loja -')
        )
      }),
      grupos: register.empresasGrupo.filter((lojaGrupo) => {
        return optionsSelect.empresasGrupos.some(
          (x) =>
            x.idGrupoELoja === lojaGrupo && x.descricao?.startsWith('Grupo -')
        )
      }),
      tipoOperacaoId: register.tipoOperacaoId,
      tipoCampanhaCrmId: register.tipoCampanhaCrmId,
      dataInicioCampanha: register.dataInicioCampanha,
      dataFimCampanha: register.dataFimCampanha,
      quantidadeClientes: register.quantidadeClientes,
      ufCampanha: register.ufCampanha,
      cidadeCampanha: cidadesCampanhaPrevia,
      redudancia: register.redudancia,
      ignorarClientesCarteira: register.cliente
        ? true
        : register.ignorarClientesCarteira,
      ignorarEnviadosElevenLabs: register.ignorarEnviadosElevenLabs,
      indisponibilidade: register.indisponibilidade,
      naoPerturbe: register.naoPerturbe,
      naoPertube: register.naoPerturbe,
      telefone: register.telefone,
      idadeInicial: register.idadeInicial,
      idadeFinal: register.idadeFinal,
      formaRecebimentoBeneficio: register.formaRecebimentoBeneficio,
      cartaoRmc: register.cartaoRmc,
      cartaoRcc: register.cartaoRcc,
      bancosCartaoRc: register.bancosCartaoRc,
      despachoAnoInicial: register.despachoAnoInicial
        ? new Date(Date.parse(register.despachoAnoInicial))
        : null,
      especies: register.especies,
      despachoAnoFinal: register.despachoAnoFinal
        ? new Date(Date.parse(register.despachoAnoFinal))
        : null,
      bancosPagadoresConsiderarCampanha:
        register.bancosPagadoresConsiderarCampanha,
      quantidadeEmprestimo: register.quantidadeEmprestimo,
      quantidadeInicialEmprestimo: register.quantidadeInicialEmprestimo,
      quantidadeFinalEmprestimo: register.quantidadeFinalEmprestimo,
      salario: register.salario,
      quantidadeInicialSalario: register.quantidadeInicialSalario
        ? Number(removeMaskMoney(register.quantidadeInicialSalario))
        : null,
      quantidadeFinalSalario: register.quantidadeFinalSalario
        ? Number(removeMaskMoney(register.quantidadeFinalSalario))
        : null,
      margemLivre: register.margemLivre,
      quantidadeInicialMargem: register.quantidadeInicialMargem
        ? Number(removeMaskMoney(register.quantidadeInicialMargem))
        : null,
      quantidadeFinalMargem: register.quantidadeFinalMargem
        ? Number(removeMaskMoney(register.quantidadeFinalMargem))
        : null,
      margemCartao: register.margemCartao,
      quantidadeInicialMargemCartao: register.quantidadeInicialMargemCartao
        ? Number(removeMaskMoney(register.quantidadeInicialMargemCartao))
        : null,
      quantidadeFinalMargemCartao: register.quantidadeFinalMargemCartao
        ? Number(removeMaskMoney(register.quantidadeFinalMargemCartao))
        : null,
      limiteCartao: register.limiteCartao || register.margemCartao,
      quantidadeInicialLimiteCartao: register.quantidadeInicialLimiteCartao
        ? Number(removeMaskMoney(register.quantidadeInicialLimiteCartao))
        : register.quantidadeInicialMargemCartao
          ? Number(removeMaskMoney(register.quantidadeInicialMargemCartao))
          : null,
      quantidadeFinalLimiteCartao: register.quantidadeFinalLimiteCartao
        ? Number(removeMaskMoney(register.quantidadeFinalLimiteCartao))
        : register.quantidadeFinalMargemCartao
          ? Number(removeMaskMoney(register.quantidadeFinalMargemCartao))
          : null,
      instituicaoFinanceiraCampanha: register.instituicaoFinanceiraCampanha,
      parcelas: register.parcelas,
      quantidadeInicialParcelas: register.quantidadeInicialParcelas,
      quantidadeFinalParcelas: register.quantidadeFinalParcelas,
      valorParcela: register.valorParcela,
      quantidadeInicialValorParcela: register.quantidadeInicialValorParcela
        ? Number(removeMaskMoney(register.quantidadeInicialValorParcela))
        : null,
      quantidadeFinalValorParcela: register.quantidadeFinalValorParcela
        ? Number(removeMaskMoney(register.quantidadeFinalValorParcela))
        : null,
      representanteLegal: register.representanteLegal,
      campanhaCrmParametrosDiscador: {
        horaInicio: register.campanhaCrmParametrosDiscador.horaInicio,
        horaFim: register.campanhaCrmParametrosDiscador.horaFim,
        tocarAudio: register.campanhaCrmParametrosDiscador.tocarAudio,
        habilitarAmd: register.campanhaCrmParametrosDiscador.habilitarAmd,
        maxDiscagensSimultaneas:
          register.campanhaCrmParametrosDiscador.maxDiscagensSimultaneas,
        fila: register.campanhaCrmParametrosDiscador.fila,
        maxDiscagensEspera:
          register.campanhaCrmParametrosDiscador.maxDiscagensEspera,
        pergunta: register.campanhaCrmParametrosDiscador.pergunta,
        esperaNaFila: register.campanhaCrmParametrosDiscador.esperaNaFila,
        discagemPorAgente:
          register.campanhaCrmParametrosDiscador.discagemPorAgente,
        pabxipCategoriaSaida:
          register.campanhaCrmParametrosDiscador.pabxipCategoriaSaida
      },
      ...(register.tipoCampanhaCrmId === '58dbb40f-ff45-4a29-8375-fcc9bb726d1f'
        ? {
            campanhaCrmParametrosElevenLabs: {
              agenteId: register.agentesElevenLabsId,
              numeroOrigemId: register.numerosOrigemElevenLabsId,
              agendarPara: register.agendarPara,
              variaveisDinamicas: register.variaveis,
              tempoLimiteToque: register.tempoLimiteToque,
              limiteConcorrencia: register.limiteConcorrencia
            }
          }
        : {})
    }

    const response = yield call(
      campanhaController.getPreviaClientesPorLoja,
      data
    )

    const content = response?.data?.content
    const listaRaw = Array.isArray(content)
      ? content
      : Array.isArray(content?.registros)
        ? content?.registros
        : []

    const lista = listaRaw.map(
      (item: {
        id?: string
        lojaId?: string
        idLoja?: string
        nome?: string
        nomeLoja?: string
        loja?: string
        descricaoLoja?: string
        quantidadeClientes?: number
        quantidade?: number
        totalClientes?: number
      }) => ({
        lojaId: item.lojaId ?? item.idLoja ?? item.id ?? '',
        nomeLoja:
          item.nomeLoja ?? item.nome ?? item.loja ?? item.descricaoLoja ?? '-',
        quantidadeClientes:
          item.quantidadeClientes ?? item.quantidade ?? item.totalClientes ?? 0
      })
    )

    yield put(campanhaActions.setPreviaClientesPorLoja(lista))
    yield put(campanhaActions.toggleModalAprovacaoClientes(true))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getEspeciesBeneficio(): SagaIterator {
  try {
    const response = yield call(campanhaController.getEspecies)
    yield put(
      campanhaActions.setOptionsSelect(response?.data?.content, 'especies')
    )
  } catch (error) {
    handlerError(error)
  }
}

function* getAllLojas(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))

    const response = yield call(campanhaController.getAllLojas)

    yield put(campanhaActions.setLojas(response?.data?.content))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}
function* confirmarAcaoCampanha(): SagaIterator {
  try {
    const { acaoModal, codigoCampanhaDiscador }: CAMPANHA = yield select(
      (state: ApplicationState) => state.campanha
    )
    yield put(loadingActions.setLoading(true))

    if (acaoModal === 'iniciar') {
      yield call(campanhaController.AtivarCampanha, codigoCampanhaDiscador)
    } else if (acaoModal === 'pausar') {
      yield call(campanhaController.DesativarCampanha, codigoCampanhaDiscador)
    } else if (acaoModal === 'tentar-novamente') {
      yield call(
        campanhaController.reprocessarCampanhaElevenLabs,
        codigoCampanhaDiscador
      )
      toast.success('Rediscagem executada com sucesso!')
    } else if (acaoModal === 'cancelar-campanha') {
      yield call(
        campanhaController.cancelarCampanhaElevenLabs,
        codigoCampanhaDiscador
      )
      toast.success('Campanha cancelada com sucesso!')
    }

    yield put(campanhaActions.getAllCampanhasSaga())
    yield put(campanhaActions.toggleModalAtivarDesativarDispatch(false))
    yield put(campanhaActions.setItemSelectedIdDispatch(''))
    yield put(campanhaActions.setAcaoModalDispatch(null))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}
function* finalizarCampanha(): SagaIterator {
  try {
    const { codigoCampanhaDiscador }: CAMPANHA = yield select(
      (state: ApplicationState) => state.campanha
    )
    if (!codigoCampanhaDiscador) return

    yield put(loadingActions.setLoading(true))

    yield call(campanhaController.finalizarCampanha, codigoCampanhaDiscador)
    yield put(campanhaActions.toggleModalFinalizarDispatch(false))
    yield put(campanhaActions.getAllCampanhasSaga())
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getTiposCampanha(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(campanhaController.getTiposCampanha)
    yield put(campanhaActions.setTiposCampanha(response?.data?.content))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}
interface GetPerguntasPayload extends GenericProps {
  codigoEmpresa: string
}

export function* getPerguntas({
  codigoEmpresa
}: GetPerguntasPayload): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))

    const response = yield call(campanhaController.getPerguntas, codigoEmpresa)

    yield put(campanhaActions.setPerguntas(response?.data?.content))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}
interface GetCategorias extends GenericProps {
  codigoEmpresa: string
}

export function* getCategorias({ codigoEmpresa }: GetCategorias): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(
      campanhaController.getCategoriasDiscador,
      codigoEmpresa
    )
    yield put(campanhaActions.setCategoriasDiscador(response?.data?.content))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getAgentesElevenLabs(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(campanhaController.getAgentesElevenLabs)
    yield put(campanhaActions.setAgentesElevenLabs(response?.data?.content))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getNumerosOrigemElevenLabs(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(campanhaController.getNumerosOrigemElevenLabs)
    yield put(
      campanhaActions.setNumerosOrigemElevenLabs(response?.data?.content)
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

export function* watchGetAllCampanhas() {
  yield takeEvery(TYPES.GET_ALL_CAMPANHAS_SAGA, getAllCampanhas)
}

export function* watchGetCampanha() {
  yield takeEvery(TYPES.GET_CAMPANHA_SAGA, getCampanha)
}

export function* watchVisualizarCampanha() {
  yield takeEvery(TYPES.VISUALIZAR_CAMPANHA_SAGA_DISPATCH, visualizarCampanha)
}

export function* watchEditarCampanha() {
  yield takeEvery(TYPES.EDITAR_CAMPANHA_SAGA_DISPATCH, editarCampanha)
}

export function* watchCriarCampanha() {
  yield takeEvery(TYPES.POST_CAMPANHA_SAGA, criarCampanha)
}

export function* watchAtualizarCampanha() {
  yield takeEvery(TYPES.PUT_CAMPANHA_SAGA, atualizarCampanha)
}

export function* watchDeletarCampanha() {
  yield takeEvery(TYPES.DELETE_CAMPANHA_SAGA, deletarCampanha)
}

export function* watchGetCidades() {
  yield takeEvery(TYPES.GET_CIDADES_SAGA, getCidades)
}

export function* watchGetEstados() {
  yield takeEvery(TYPES.GET_ESTADOS_SAGA, getEstados)
}

export function* watchGetGeneros() {
  yield takeEvery(TYPES.GET_GENEROS_SAGA, getGeneros)
}

export function* watchGetTiposOperacao() {
  yield takeEvery(TYPES.GET_TIPOS_OPERACO_SAGA, getTiposOperacao)
}

export function* watchGetBancos() {
  yield takeEvery(TYPES.GET_BANCOS_SAGA, getBancos)
}

export function* watchGetGruposLojas() {
  yield takeEvery(TYPES.GET_GRUPOS_LOJAS_SAGA, getGruposLojas)
}

export function* watchGetBancosCartao() {
  yield takeEvery(TYPES.GET_BANCOS_CARTAO_SAGA, getBancosCartao)
}

export function* watchAlterarStatusCampanha() {
  yield takeEvery(
    TYPES.ALTERAR_STATUS_CAMPANHA_DISPATCH_SAGA,
    alterarStatusCampanha
  )
}

export function* watchAbastecimentoClientes() {
  // yield takeEvery(TYPES.ABASTECIMENTO_CLIENTES_SAGA, abastecimentoClientes)
}

export function* watchReabastecimentoClientes() {
  // yield takeEvery(TYPES.REABASTECIMENTO_CLIENTES_SAGA, reabastecimentoClientes)
}

export function* watchGetEspeciesBeneficio() {
  yield takeEvery(TYPES.GET_ESPECIES_BENEFICIO_SAGA, getEspeciesBeneficio)
}

export function* watchGetAllLojas() {
  yield takeEvery(TYPES.GET_ALL_LOJAS_SAGA, getAllLojas)
}

export function* watchGetTiposCampanha() {
  yield takeEvery(TYPES.GET_TIPOS_CAMPANHAS_SAGA, getTiposCampanha)
}

export function* watchGetPerguntas() {
  yield takeEvery(TYPES.GET_PERGUNTAS_SAGA, getPerguntas)
}
export function* watchGetCategorias() {
  yield takeEvery(TYPES.GET_CATEGORIAS_DISCADOR_SAGA, getCategorias)
}

export function* watchGetAgentesElevenLabs() {
  yield takeEvery(TYPES.GET_AGENTES_ELEVENLABS_SAGA, getAgentesElevenLabs)
}

export function* watchGetNumerosOrigemElevenLabs() {
  yield takeEvery(
    TYPES.GET_NUMEROS_ORIGEM_ELEVENLABS_SAGA,
    getNumerosOrigemElevenLabs
  )
}

export function* watchDeletarCampanhasSelecionadas() {
  yield takeEvery(
    TYPES.DELETE_CAMPANHAS_SELECIONADAS_SAGA,
    deletarCampanhasSelecionadas
  )
}
export function* watchConfirmarAcaoCampanha() {
  yield takeEvery(TYPES.CONFIRMAR_ACAO_CAMPANHA_SAGA, confirmarAcaoCampanha)
}

export function* watchFinalizarCampanha() {
  yield takeEvery(TYPES.FINALIZAR_CAMPANHA_SAGA, finalizarCampanha)
}

export function* watchGetPreviaClientesPorLoja() {
  yield takeEvery(
    TYPES.GET_PREVIA_CLIENTES_POR_LOJA_SAGA,
    getPreviaClientesPorLoja
  )
}

function* campanhaSaga() {
  yield all([
    fork(watchGetAllCampanhas),
    fork(watchGetCampanha),
    fork(watchVisualizarCampanha),
    fork(watchEditarCampanha),
    fork(watchCriarCampanha),
    fork(watchAtualizarCampanha),
    fork(watchDeletarCampanha),
    fork(watchGetCidades),
    fork(watchGetEstados),
    fork(watchGetGeneros),
    fork(watchGetTiposOperacao),
    fork(watchGetBancos),
    fork(watchGetGruposLojas),
    fork(watchGetBancosCartao),
    fork(watchAlterarStatusCampanha),
    // fork(watchAbastecimentoClientes),
    // fork(watchReabastecimentoClientes),
    fork(watchGetEspeciesBeneficio),
    fork(watchGetAllLojas),
    fork(watchGetTiposCampanha),
    fork(watchGetPerguntas),
    fork(watchGetCategorias),
    fork(watchGetAgentesElevenLabs),
    fork(watchGetNumerosOrigemElevenLabs),
    fork(watchDeletarCampanhasSelecionadas),
    fork(watchConfirmarAcaoCampanha),
    fork(watchFinalizarCampanha),
    fork(watchGetPreviaClientesPorLoja)
  ])
}

export default campanhaSaga
